import "./Pricelist.scss";

import { EuiFlexItem } from "@elastic/eui";
import { PriceLegendNew } from "components/LegendNew";
import { PriceRowNew } from "components/PriceRowNew";
import { PriceTableHeaderNew } from "components/PriceTableHeaderNew";
import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";

import client from "../api/Client";
import { BannerAll } from "../components/BannerAll";
import { BottomBar } from "../components/BottomBar";
import { PriceLegend } from "../components/Legend";
import API_URL, { WEBSOCKET_URL } from "../Constants";
import { filterDeactivatedJsonPrices, isPortrait } from "../utils/PricelistUtils";
import { JsonPrice, Price, PricelistColumns } from "../utils/types";
import { getCategoryNameFromPrices } from "../utils/Utils";
import { deSteegStyle } from "./pricelist-styles/DeSteegStyle";
import { easyGoingStyle } from "./pricelist-styles/EasyGoingStyle";
import { easyGoingStyleVertical } from "./pricelist-styles/EasyGoingStyleVertical";
import { maryJaneStyle } from "./pricelist-styles/MaryJaneStyle";
import { maryJaneStyleVertical } from "./pricelist-styles/MaryJaneStyleVertical";
import { mississippiStyle } from "./pricelist-styles/MississippiStyle";
import { mississippiStyleVertical } from "./pricelist-styles/MississippiStyleVertical";
import { missouriStyle } from "./pricelist-styles/MissouriStyle";
import { missouriStyleVertical } from "./pricelist-styles/MissouriStyleVertical";
import { roundabout69Style } from "./pricelist-styles/RoundAbout69Style";
import { roundabout69StyleLight } from "./pricelist-styles/RoundAbout69StyleLight";

interface PricelistCategorySection {
    category_pricelist_column: PricelistColumns;
    category_pricelist_row: number;
    category_name: string;
    prices: Price[];
}

const PriceListNew = () => {
    const { shopId } = useParams();
    const [prices, setPrices] = useState<Price[]>([]);
    const [cacheDate, setCacheDate] = useState("");

    const styles = (() => {
        switch (shopId) {
            case "bbc0cfa7-e230-4338-817a-55190debdac0":
                return isPortrait ? maryJaneStyleVertical : maryJaneStyle;
            case "a08a13e2-a31b-4b6d-b2b4-0491cb3d2227":
                return isPortrait ? missouriStyleVertical : missouriStyle;
            case "ddd70853-5e9d-45ff-82b3-24c9d81d4e32":
                return isPortrait ? easyGoingStyleVertical : easyGoingStyle;
            case "470f3f5a-e7b9-43a5-bbfd-2ffce8c161e4":
                return deSteegStyle;
            case "f155b35a-14a1-4866-867f-93fe21e9769b":
                if (window.location.pathname.includes("light")) {
                    return roundabout69StyleLight;
                }
                return roundabout69Style;
            default:
                return isPortrait ? mississippiStyleVertical : mississippiStyle;
        }
    })();

    const defaultColumnOrder = ["g", "j", "st"];

    const refreshData = useCallback(() => {
        console.log("Refreshing data");
        client.get(`${API_URL}/v1/shops/${shopId}`).then((result) => {
            const cannabis = filterDeactivatedJsonPrices(result?.data?.prices ?? []);

            if (window.location.pathname.includes("extra")) {
                cannabis
                    .filter((price: Price) => price.active)
                    .filter(
                        (price: Price) =>
                            price.category_name.toLowerCase() === "edibles" ||
                            price.category_id === "9a1d6ba6-79cc-412b-be47-ac30772cda9b"
                    );
                client.get(`${API_URL}/v1/shops/${shopId}?is_horeca=true`).then((result) => {
                    setPrices(cannabis.concat(cannabis.filter((price: Price) => price.active)));
                });
            } else {
                setPrices(
                    cannabis
                        .filter((price: Price) => price.active)
                        .filter((price: Price) => price.category_name.toLowerCase() !== "edibles")
                        .filter((price: Price) => price.category_id !== "9a1d6ba6-79cc-412b-be47-ac30772cda9b")
                );
            }
        });
    }, [shopId]);

    const connectWebSocket = useCallback(() => {
        const socket = new WebSocket(`${WEBSOCKET_URL}?shopId=${shopId}&connectionType=shop`);

        socket.onopen = () => {
            console.log("Connection with websocket server established.", socket);
            // setConnectedToWebSocket(true);
        };
        socket.onerror = () => {
            console.log("Error connecting to websocket server, falling back to polling.");
            // setConnectedToWebSocket(false);
            // setInterval(checkCacheDate, 5000);
        };
        socket.onmessage = (event) => {
            console.log(`[message] Data received from server: ${event.data}`);
            console.log(JSON.parse(event.data));
            if (JSON.parse(event.data) === "invalidate_shop_cache") {
                refreshData();
            }
        };
        socket.onclose = () => {
            console.log("Re-spawning websocket");
            connectWebSocket();
        };
    }, [shopId, refreshData]);

    const checkCacheDate = useCallback(() => {
        client.get(`${API_URL}/v1/shops/cache-status/${shopId}`).then((result) => {
            const newCacheDate = result.data.modified_at;

            if (cacheDate !== newCacheDate) {
                console.log("Fallback cache check found a difference!");
                setCacheDate(newCacheDate);
                refreshData();
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shopId, refreshData]);

    useEffect(() => {
        client.get(`${API_URL}/v1/shops/cache-status/${shopId}`).then((result) => {
            setInterval(checkCacheDate, 60000);
        });
        refreshData();
        connectWebSocket();
    }, [checkCacheDate, connectWebSocket, shopId, refreshData]);

    const getFirstNotEmptyCategory = (categories: Price[][]) => {
        for (let category of Object.values(categories)) {
            if (category.length > 0) {
                return category[0].category_name;
            }
        }
    };

    let uniqueCategories: PricelistCategorySection[] = [];
    let leftCategories: PricelistCategorySection[] = [];
    let rightCategories: PricelistCategorySection[] = [];

    //Get unique categories from the array of all prices
    for (const price of prices) {
        const { category_name, category_pricelist_column, category_pricelist_row } = price;

        if (
            (price.active && price.kind_id !== null && !window.location.pathname.includes("extra")) ||
            (price.active && window.location.pathname.includes("extra"))
        ) {
            if (!uniqueCategories.some((category) => category.category_name === category_name)) {
                uniqueCategories.push({
                    category_pricelist_column,
                    category_pricelist_row,
                    category_name,
                    prices: [],
                });
            }

            //Push price to a unique category
            const foundCategory = uniqueCategories.find((category) => category.category_name === category_name);
            foundCategory && foundCategory.prices.push(price);
        }
    }
    //Sort unique categories into left and right
    for (const category of uniqueCategories) {
        if (
            category.category_pricelist_column === PricelistColumns.LEFT ||
            (category.category_name.toLowerCase() === "prerolled joints" && window.location.pathname.includes("extra"))
        ) {
            leftCategories.push(category);
            uniqueCategories = uniqueCategories.filter((cat) => cat.category_name !== category.category_name);
        } else if (category.category_pricelist_column === PricelistColumns.RIGHT) {
            rightCategories.push(category);
            uniqueCategories = uniqueCategories.filter((cat) => cat.category_name !== category.category_name);
        }
    }

    //Auto sorting if no left or right is specified in the database
    if (leftCategories.length === 0) {
        const result = uniqueCategories.reduce((maxObj, currentObj) => {
            return currentObj.prices.length > maxObj.prices.length ? currentObj : maxObj;
        }, uniqueCategories[0]);
        result && leftCategories.push(result);
        uniqueCategories = uniqueCategories.filter((c) => c.category_name !== result.category_name);
        rightCategories.push(...uniqueCategories);
    } else {
        rightCategories.push(...uniqueCategories);
    }

    //Finally sort by row numberc
    const sortByTableIndexAndName = (arr: PricelistCategorySection[]) => {
        return arr.sort((a, b) => {
            if (a.category_pricelist_row === null && b.category_pricelist_row === null) {
                return a.category_name.localeCompare(b.category_name);
            } else if (a.category_pricelist_row === null) {
                return 1;
            } else if (b.category_pricelist_row === null) {
                return -1;
            } else {
                return a.category_pricelist_row - b.category_pricelist_row;
            }
        });
    };

    rightCategories = sortByTableIndexAndName(rightCategories);
    leftCategories = sortByTableIndexAndName(leftCategories);

    const leftSide: Price[][] = leftCategories.length > 0 ? leftCategories.map((price) => price.prices) : [];
    const rightSide: Price[][] = rightCategories.length > 0 ? rightCategories.map((price) => price.prices) : [];
    // const firstLeftPrice = leftCategories.length > 0 ? leftCategories[0].prices : [];

    const extractUniqueLabels = (categories: any[]): string[] => {
        const allLabels = categories.flatMap((cat) =>
            cat.prices.flatMap((price: Price) =>
                price.json_prices ? price.json_prices.map((jp: JsonPrice) => jp.label) : ["Stuk"]
            )
        );

        console.log("allLabels", allLabels);

        const uniqueLabels = Array.from(new Set(allLabels));

        console.log("uniqueLabels", uniqueLabels);

        return uniqueLabels;
    };

    const getCustomColumnNames = (shopId: string): Record<string, string> => {
        const customMappings: Record<string, Record<string, string>> = {
            "bbc0cfa7-e230-4338-817a-55190debdac0": {},
            "a08a13e2-a31b-4b6d-b2b4-0491cb3d2227": {},
            "ddd70853-5e9d-45ff-82b3-24c9d81d4e32": {},
            "19149768-691c-40d8-a08e-fe900fd23bc0": {},
        };

        return customMappings[shopId] || {};
    };

    const shopColumnNames = getCustomColumnNames(shopId);
    const leftColsName = extractUniqueLabels(leftCategories).map((label) => shopColumnNames[label] || label);
    const rightColsName = extractUniqueLabels(rightCategories).map((label) => shopColumnNames[label] || label);

    const sortBySuffixOrder = (labels: string[], order: string[]): string[] => {
        const parseLabel = (label: string) => {
            const match = label.match(/^([\d.]+)([a-zA-Z]+)$/);
            if (!match) return { quantity: 0, suffix: "" };
            return {
                quantity: parseFloat(match[1]),
                suffix: match[2],
            };
        };

        return [...labels].sort((a, b) => {
            const aParsed = parseLabel(a);
            const bParsed = parseLabel(b);

            const suffixIndexA = order.indexOf(aParsed.suffix);
            const suffixIndexB = order.indexOf(bParsed.suffix);

            if (suffixIndexA !== suffixIndexB) {
                return suffixIndexA - suffixIndexB;
            }

            return aParsed.quantity - bParsed.quantity;
        });
    };

    const Categories = ({ categories }: { categories: Price[][] }) => {
        return (
            <>
                {Object.values(categories).map((prices) => {
                    if (prices.length > 0) {
                        console.log(prices);
                        const cols: string[] = [];

                        prices.forEach((price) => {
                            if (price.json_prices) {
                                price.json_prices.forEach((jp) => {
                                    if (shopColumnNames[jp.label]) {
                                        jp.label = shopColumnNames[jp.label];
                                    }
                                    if (!cols.includes(jp.label)) {
                                        cols.push(jp.label);
                                    }
                                });
                            } else {
                                if (!cols.includes("Stuk")) {
                                    cols.push("Stuk");
                                }
                            }
                        });

                        const sortedCols = sortBySuffixOrder(cols, defaultColumnOrder);

                        return (
                            <div key={getCategoryNameFromPrices(prices)}>
                                <PriceTableHeaderNew
                                    title={getCategoryNameFromPrices(prices)}
                                    cols={sortedCols}
                                    showColumsHeader={
                                        getFirstNotEmptyCategory(categories) === getCategoryNameFromPrices(prices)
                                    }
                                    topHeader={
                                        getFirstNotEmptyCategory(categories) === getCategoryNameFromPrices(prices)
                                    }
                                />
                                <div className="backgroundShadow">
                                    <PriceRowNew prices={prices} cols={sortedCols} shopId={shopId} />
                                </div>
                            </div>
                        );
                    }
                    return null;
                })}
            </>
        );
    };

    return (
        <EuiFlexItem css={styles}>
            <div className={window.location.pathname.includes("extra") ? "horecapagebackground" : "pagebackground"}>
                <BottomBar />
                <BannerAll shopId={shopId} />
                <div className="pattern" />
                <embed className="logo" src="/images/pricelist/logo.png" />
                <div className="menukaart">
                    <div className="menulinks">
                        <div className="marginTopTitle">
                            <Categories categories={leftSide} colsName={leftColsName} />
                        </div>
                    </div>
                    <div className="menurechts">
                        <div className="marginTopTitle">
                            <Categories categories={rightSide} colsName={rightColsName} />
                            <PriceLegend shopId={shopId} />
                        </div>
                    </div>
                </div>
            </div>
            <PriceLegendNew shopId={shopId} />
        </EuiFlexItem>
    );
};

export default PriceListNew;
